import React from 'react';

import dayjs from 'dayjs';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { IoClose } from 'react-icons/io5';

import { Button } from '../input';
import { removeEmpty, replaceAllString, jsUcFirst } from '../utils';

const splitArray = (array) => {
  const x = array.slice(0, 3);
  const y = array.slice(3, array.length);
  return [x, y];
};

const dataToRender = (isArray, data) => {
  if (isArray) {
    const [first, second] = splitArray(data);
    return { first, second };
  }

  return data;
};

const renderFilterPill = (item, cb) => {
  const isArray = Array.isArray(item?.data);
  const data = dataToRender(isArray, item?.data);

  const handleOnClick = (e) => {
    e.preventDefault();
    cb();
  };

  if (item?.data?.value === '') return null;

  const renderDefault = (row) => {
    if (row?.from || row?.to) {
      const newFrom = dayjs(row?.from).format('MMM DD, YYYY');
      const newTo = dayjs(row?.to).format('MMM DD, YYYY');

      if (row?.from === row?.to) return newFrom;

      return `${newFrom} - ${newTo}`;
    }

    return (
      <span>
        {typeof data === 'string'
          ? jsUcFirst(data?.replaceAll('_', ' '))
          : jsUcFirst(data?.label?.replaceAll('_', ' '))}
      </span>
    );
  };

  return (
    <div className="bg-gray-200 rounded-full px-3 py-1 text-gray-700 font-bold text-xs flex items-center gap-1 cursor-pointer">
      <span className="capitalize">{replaceAllString(item?.id, '_', ' ')}</span>
      is
      {isArray ? (
        <div
          className={cn({
            'has-tooltip': data?.second.length,
          })}
        >
          <span className="tooltip rounded shadow-lg p-2 bg-gray-900 bg-opacity-80 text-white mt-5 text-xs">
            {data?.second?.map((x) => ` ${x?.label}`).join()}
          </span>
          <span>
            {data?.first?.map((x) => ` ${jsUcFirst(x?.label)}`).join()}
          </span>
          <span className="hover:underline">
            {data?.second.length ? ` +${data?.second.length} more...` : ''}
          </span>
        </div>
      ) : (
        <span>{renderDefault(data)}</span>
      )}
      <a href="/" onClick={handleOnClick} className="hover:text-primary-500">
        <IoClose className="h-4 w-4" />
      </a>
    </div>
  );
};

function FitlerResult({ filter, onClear, onDelete, subTitle }) {
  const filterValues = Object.keys(filter)
    .map((item) => ({
      id: item,
      data: filter[item],
    }))
    .filter((item) => item?.data !== '' && !isEmpty(item?.data));
  const hasFilter = filterValues.length;

  const handleOnDelete = (key) => () => {
    const args = {
      ...filter,
      [key]: '',
    };
    onDelete(args);
  };

  return (
    <>
      <div className="hidden md:block">
        <div className="border border-gray-200 bg-white rounded py-3 px-6">
          <div className="flex items-center gap-3">
            <div>
              <h4 className="text-gray-900 text-xs font-semibold">
                ACTIVE FILTERS
              </h4>
            </div>
            <div className="flex-1 flex gap-2 flex-wrap">
              {hasFilter ? (
                <>
                  {filterValues
                    .filter((item) => !isEmpty(removeEmpty(item?.data)))
                    .map((item) => (
                      <React.Fragment key={item?.id}>
                        {renderFilterPill(item, handleOnDelete(item?.id))}
                      </React.Fragment>
                    ))}
                </>
              ) : (
                <span className="text-gray-400 font-normal text-xs py-1">
                  No active filter
                </span>
              )}
              {subTitle && <span>{subTitle}</span>}
            </div>
            <Button
              isLoading={false}
              className="btn outline-light sm"
              label="Reset Filters"
              onClick={onClear}
            />
          </div>
        </div>
      </div>
      <div className="block md:hidden">
        <div className="flex items-center">
          <h4 className="text-gray-900 text-xs font-semibold flex items-center gap-1.5">
            ACTIVE FILTERS{' '}
            {filterValues?.length > 0 && `(${filterValues?.length})`}
            {subTitle && <span>{subTitle}</span>}
          </h4>
          <div className="ml-auto">
            <Button
              isLoading={false}
              className="btn outline-light sm"
              label="Reset Filters"
              onClick={onClear}
            />
          </div>
        </div>
      </div>
    </>
  );
}

FitlerResult.defaultProps = {
  filter: {},
  onClear: () => {},
  onDelete: () => {},
  subTitle: false,
};

FitlerResult.propTypes = {
  onClear: PropTypes.instanceOf(Function),
  filter: PropTypes.instanceOf(Object),
  onDelete: PropTypes.instanceOf(Function),
  subTitle: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
};

export default FitlerResult;
